import { usePollingInterval } from 'src/hooks/usePollingInterval'
import { CardContent, makeStyles, Button, Typography, useIsFirstRender } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { ActivityTimeline, ActivityActions, ActivityAction, useDialogs } from '@paintscout/ui-business'
import type { ActivityTimelineProps } from '@paintscout/ui-business'
import type { Activity, SearchActivitiesQuery } from '@paintscout/api'
import { useSearchActivitiesQuery } from '@paintscout/api'
import type { EsFilter } from 'json-to-lucene'
import ActivityLink from 'src/components/common/ActivityLink'
import React, { useCallback, useEffect, useMemo } from 'react'
import * as Sentry from '@sentry/react'
import EyeIcon from '@material-ui/icons/Visibility'

export interface DashboardActivityProps {
  query?: string
  filters?: EsFilter[]
  onUpdate?: () => void
  showContent?: boolean
  showClear?: boolean
  ActivityComponent?: ActivityTimelineProps['ActivityComponent']
  noResultsMessage?: string
  onDataReceived?: (data: SearchActivitiesQuery) => any
  onClear?: () => void
  title?: string
  newTheme?: boolean
  onAction?: (action: string, activity: Activity) => any
  actions?: boolean
  disabled?: boolean
  skipSanitize?: boolean
  /**
   * The document being viewed (deal/contact/company). When set, notes pinned on this doc
   * (`details.pinnedOn`) sort to the top and render in a "Pinned" section. Only the
   * deal/contact/company notes feed passes this; other feeds stay timestamp-only. See #21136.
   */
  pinnedDocId?: string
}

const useStyles = makeStyles((theme: Theme) => ({
  root: () => {
    return {
      position: 'relative'
    }
  },
  noActivityMessage: {
    height: 160, // match size of estimates table with no results
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'pre-wrap'
  },
  clearButton: {
    position: 'absolute',
    top: theme.spacing(2),
    right: theme.spacing(3),
    zIndex: 1
  },
  title: {
    marginBottom: theme.spacing(2)
  }
}))

export default function DashboardActivity({
  query,
  filters,
  onUpdate,
  showContent,
  showClear,
  ActivityComponent = ActivityLink,
  noResultsMessage = 'No activity found.',
  onClear,
  onDataReceived,
  title,
  onAction,
  actions = false,
  disabled = false,
  skipSanitize = false,
  pinnedDocId
}: DashboardActivityProps) {
  const classes = useStyles({})
  const { stack } = useDialogs()
  const pollingInterval = usePollingInterval(120000)
  const isFirstRender = useIsFirstRender()
  const { loading, data, fetchMore, updateQuery } = useSearchActivitiesQuery({
    fetchPolicy: 'cache-and-network',
    pollInterval: stack.length > 0 ? null : pollingInterval,
    notifyOnNetworkStatusChange: false,
    variables: {
      query: query ?? '',
      filters,
      limit: 10,
      skipSanitize,
      ...(pinnedDocId ? { pinnedForDocId: pinnedDocId } : {})
    },
    onCompleted: useCallback((data) => {
      onUpdate?.()
      onDataReceived?.(data)
    }, []),
    onError: useCallback((error) => {
      Sentry.captureException(error)
    }, [])
  })

  // `filters` is often a fresh array literal from the caller on every render (see #23565), so
  // comparing it by reference busts the cache on unrelated re-renders. Key off its content instead.
  // Order-sensitive: two filter arrays with the same entries in a different order serialize
  // differently and would still be treated as a change. Callers currently build these as static
  // literals with a fixed key order, so this hasn't come up in practice.
  const filtersKey = useMemo(() => JSON.stringify({ query, filters }), [query, filters])

  // bust cache if query/filters change after first query has loaded
  // Deliberately depends on `filtersKey` alone, not `query`/`filters` directly — it already
  // encodes both. If a new value needs to trigger this effect, fold it into filtersKey above
  // rather than adding it to this dependency array.
  useEffect(() => {
    if (!isFirstRender) {
      updateQuery((prev) => {
        return {
          ...prev,
          searchActivities: null
        }
      })
    }
  }, [filtersKey])

  const rows = data?.searchActivities?.activities ?? []
  // Derived server-side from whether the last fetched page filled the limit, so it's not
  // thrown off by activities filtered out after the search (see #20806).
  const hasMore = data?.searchActivities?.hasMore ?? false

  const showClearButton = !loading && showClear && !!onClear

  return (
    <div className={classes.root}>
      <CardContent>
        <>
          {title && (
            <Typography variant="h2" className={classes.title}>
              {title}
            </Typography>
          )}
          {showClearButton && (
            <div className={classes.clearButton}>
              <ActivityActions>
                <ActivityAction onClick={onClear} icon={EyeIcon}>
                  Mark all as read
                </ActivityAction>
              </ActivityActions>
            </div>
          )}
          {!loading && rows.length === 0 && (
            <Typography variant="subtitle1" align="center" className={classes.noActivityMessage}>
              {noResultsMessage}
            </Typography>
          )}

          <ActivityTimeline
            loading={loading && !data}
            activities={rows}
            showDocTypeDetails
            showContent={showContent}
            ActivityComponent={ActivityComponent}
            actions={actions}
            onAction={onAction}
            disabled={disabled}
            pinnedDocId={pinnedDocId}
          />
          {hasMore && (
            <Button
              variant="text"
              fullWidth
              onClick={() => {
                fetchMore({
                  variables: {
                    query: query ?? '',
                    filters,
                    bookmark: data.searchActivities.bookmark
                  },
                  updateQuery(prev, { fetchMoreResult }) {
                    if (!fetchMoreResult) {
                      return prev
                    }

                    return {
                      ...prev,
                      ...fetchMoreResult,
                      searchActivities: {
                        ...prev.searchActivities,
                        ...fetchMoreResult.searchActivities,
                        activities: [
                          ...prev.searchActivities.activities,
                          ...fetchMoreResult.searchActivities.activities
                        ]
                      }
                    }
                  }
                })
              }}
              disabled={loading}
            >
              View More
            </Button>
          )}
        </>
      </CardContent>
    </div>
  )
}
